import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, timer } from 'rxjs';
import {
  delayWhen,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { authActions } from '@cigna/auth/data-access';

import {
  GetLiveAvailability,
  GetLiveAvailabilitySuccess,
  LiveAvailabilityAction,
  LiveAvailabilityActionTypes,
  PauseLiveAvailabilityCheck,
  ResumeLiveAvailabilityCheck,
} from './live-availability.actions';

import { getUTCOffset } from '../utils';
import { LiveAvailabilityDataAccessService } from '@cigna/omni/data-access';
import { liveAvailabilityQuery } from './live-availability.selectors';

@Injectable()
export class LiveAvailabilityEffects {
  constructor(
    private actions$: Actions,
    private store$: Store,
    private dataAccessService: LiveAvailabilityDataAccessService,
  ) {}

  getLiveAvailability$: Observable<LiveAvailabilityAction> = createEffect(() =>
    this.actions$.pipe(
      ofType<GetLiveAvailability>(
        LiveAvailabilityActionTypes.GetLiveAvailability,
      ),
      switchMap(() =>
        this.dataAccessService
          .getLiveAvailability(
            getUTCOffset(new Date().getTimezoneOffset()),
            '12h',
          )
          .pipe(
            map(
              (liveAvailability) =>
                new GetLiveAvailabilitySuccess(liveAvailability),
            ),
          ),
      ),
    ),
  );

  getLiveAvailabilitySuccess$: Observable<
    LiveAvailabilityAction
  > = createEffect(() =>
    this.actions$.pipe(
      ofType<GetLiveAvailabilitySuccess>(
        LiveAvailabilityActionTypes.GetLiveAvailabilitySuccess,
      ),
      delayWhen(({ liveAvailability }) => {
        const validUntil = new Date(liveAvailability.validUntil);

        if (validUntil < new Date()) {
          return timer(60000);
        }
        return timer(validUntil);
      }),
      map(() => new GetLiveAvailability()),
    ),
  );

  resumeLiveAvailability$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.loadAuthSuccess),
      withLatestFrom(
        this.store$.pipe(select(liveAvailabilityQuery.isLiveCheckPaused)),
      ),
      filter(([_authSuccess, isPollPaused]) => !!isPollPaused),
      switchMap(([_authSuccess, _isPollPaused]) => [
        new GetLiveAvailability(),
        new ResumeLiveAvailabilityCheck(),
      ]),
    ),
  );

  stopPolling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.unauthorizedError),
      map(() => new PauseLiveAvailabilityCheck()),
    ),
  );
}
